const grayShades = {
  50: '#F9FAFB',
  100: '#F2F4F7',
  200: '#E4E7EC',
  300: '#D0D5DD',
  400: '#98A2B3',
  600: '#475467',
  800: '#1D2939',
  900: '#101828',
}

const greenShades = {
  100: '#E7F7EA',
  200: '#AED0BC',
  500: '#3E895B',
  600: '#2C7749',
  800: '#0F5629',
}

const yellowShades = {
  100: '#FCEDB2',
  400: '#F8D548',
  800: '#6A4600',
}

const blueShades = {
  100: '#E0F2FF',
  400: '#96CEFD',
  900: '#0054B6',
  1000: '#00518C',
}

const redShades = {
  100: '#FFEBE7',
  200: '#FFCDC3',
  500: '#EA3829',
  700: '#A50000',
}

const terracottaShades = {
  400: '#CF6558',
  500: '#C55649',
}

const orangeShades = {
  500: '#FAA11D',
}

const pinkShades = {
  100: '#FCF5F2',
  200: '#F8ECE9',
  300: '#F7E2DA',
  400: '#F2C8C5',
}

const butterShades = {
  50: '#FDFCFC',
  100: '#F9F3F0',
  200: '#F4EFEB',
  400: '#E9DBD0',
}

const colors = {
  terracotta: terracottaShades,
  pink: pinkShades,
  butter: butterShades,
  gray: grayShades,
  red: redShades,
  orange: orangeShades,
  yellow: yellowShades,
  green: greenShades,
  blue: blueShades,
  white: '#FFFFFF',
  black: '#000000',
}

export const theme = {
  palette: {
    ...colors,
    primary: {
      main: colors.terracotta[400],
      dark: colors.terracotta[500],
    },
    secondary: {
      main: colors.pink[200],
      dark: colors.pink[400],
    },
    tertiary: {
      main: colors.gray[900],
    },
    background: {
      default: colors.white,
      butter: colors.butter[100],
      butterLight: colors.butter[50],
      butterDark: colors.butter[200],
    },
    button: {
      default: {
        main: 'transparent',
        dark: 'transparent',
        contrastText: colors.terracotta[400],
        outlineColor: colors.terracotta[400],
      },
      primary: {
        main: colors.terracotta[400],
        dark: colors.terracotta[500],
        contrastText: colors.white,
        outlineColor: colors.terracotta[500],
      },
      secondary: {
        main: colors.pink[100],
        dark: colors.pink[200],
        contrastText: colors.terracotta[400],
        outlineColor: colors.pink[400],
      },
      tertiary: {
        main: colors.white,
        dark: colors.gray[100],
        contrastText: colors.gray[900],
        outlineColor: colors.gray[300],
      },
      darkGray: {
        main: colors.gray[900],
        dark: colors.gray[800],
        contrastText: colors.white,
        outlineColor: colors.gray[600],
      },
    },
    text: {
      default: colors.gray[900],
      white: colors.white,
      light: colors.gray[600],
      disabled: colors.gray[300],
      primary: colors.terracotta[400],
    },
    info: {
      light: colors.blue[100],
      main: colors.blue[900],
      contrastText: colors.blue[1000],
      outlineColor: colors.blue[400],
    },
    success: {
      light: colors.green[100],
      main: colors.green[500],
      contrastText: colors.green[800],
      outlineColor: colors.green[200],
    },
    warning: {
      light: colors.yellow[100],
      main: colors.orange[500],
      contrastText: colors.yellow[800],
      outlineColor: colors.yellow[400],
    },
    error: {
      light: colors.red[100],
      main: colors.red[500],
      contrastText: colors.red[700],
      outlineColor: colors.red[200],
    },
    overlay: {
      light: 'rgba(16, 24, 40, 0.30)',
      main: 'rgba(16, 24, 40, 0.75)',
    },
    ai: {
      blue: {main: '#40CFFF', light: '#CAEAFC', dark: '#02499e'},
      purple: {main: '#A020F0', light: '#DBCAFC'},
    },
    skeletonLoading: {
      light: colors.gray[50],
      main: colors.gray[200],
      dark: colors.gray[800],
      outlineColor: colors.gray[200],
      highlight: {
        onLight: 'rgba(255, 255, 255, 0.5)',
        onDark: 'rgba(255, 255, 255, 0.2)',
      },
    },
  },
  typography: {
    h1: {
      desktop: 'font-size: 66px; line-height: 70px; letter-spacing: -2px;',
      mobile: 'font-size: 36px; line-height: 44px; letter-spacing: -0.72px;',
    },
    h2: {
      desktop: 'font-size: 54px; line-height: 62px; letter-spacing: -1.5px;',
      mobile: 'font-size: 30px; line-height: 38px; letter-spacing: -0.32px;',
    },
    h3: {
      desktop: 'font-size: 30px; line-height: 34px; letter-spacing: -0.3px;',
      mobile: 'font-size: 24px; line-height: 32px; letter-spacing: -0.24px;',
    },
    h4: {
      desktop: 'font-size: 24px; line-height: 32px; letter-spacing: -0.3px;',
      mobile: 'font-size: 20px; line-height: 30px; letter-spacing: 0px;',
    },
    h5: {
      desktop: 'font-size: 20px; line-height: 28px;',
      mobile: 'font-size: 18px; line-height: 24px;',
    },
    h6: {
      desktop: 'font-size: 18px; line-height: 24px;',
      mobile: 'font-size: 16px; line-height: 24px;',
    },
    text43: {
      desktop: 'font-size: 43px; line-height: 48px; letter-spacing: -0.3px;',
      mobile: 'font-size: 36px; line-height: 44px; letter-spacing: -0.72px;',
    },
    text24: {
      desktop: 'font-size: 24px; line-height: 36px;',
      mobile: 'font-size: 20px; line-height: 32px;',
    },
    text20: {
      desktop: 'font-size: 20px; line-height: 36px;',
      mobile: 'font-size: 16px; line-height: 28px;',
    },
    text16: {
      desktop: 'font-size: 16px; line-height: 24px; letter-spacing: 0px;',
    },
    text14: {
      desktop: 'font-size: 14px; line-height: 20px; letter-spacing: 0px;',
    },
    subtitle14: {
      desktop:
        'font-size: 14px; line-height: 20px; letter-spacing: 0.5px; text-transform: uppercase;',
    },
    subtitle13: {
      desktop:
        'font-size: 13px; line-height: 20px; letter-spacing: 0.8px; text-transform: uppercase;',
    },
    subtitle12: {
      desktop:
        'font-size: 12px; line-height: 20px; letter-spacing: 2px; text-transform: uppercase;',
    },
    subtitle11: {
      desktop:
        'font-size: 11px; line-height: 16px; letter-spacing: 0.8px; text-transform: uppercase;',
      mobile: 'font-size: 10px;',
    },
    caption: {
      desktop: 'font-size: 12px; line-height: 16px; letter-spacing: 0px;',
    },
  },
  fontWeight: {
    light: 300,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },
  spacing: {
    0.5: '2px',
    1: '4px',
    1.5: '6px',
    2: '8px',
    2.5: '12px',
    3: '16px',
    3.5: '20px',
    4: '24px',
    4.5: '28px',
    5: '32px',
    5.5: '40px',
    6: '48px',
    6.5: '56px',
    7: '64px',
    7.5: '72px',
    8: '80px',
    8.5: '104px',
    9: '128px',
    9.5: '136px',
  },
}
